import React, { ReactNode, useEffect, useRef, useCallback } from 'react';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  Button as ButtonCK,
  useDisclosure,
} from '@chakra-ui/react';
import { Colors, Icon } from '@mercator-gcp/grout';

const PopoverTriggerWithChildren = PopoverTrigger as React.FC<{
  children?: React.ReactNode;
}>;

const accordionIconContainer = {
  width: '16px',
  minWidth: '16px',
  height: '16px',
  background: 'none',
  color: 'inherit',
  border: 'none',
  padding: 0,
  font: 'inherit',
  outline: 'inherit',
  boxShadow: 'none',
  '&:hover, &:focus': {
    background: 'none',
    color: 'inherit',
    border: 'none',
    padding: 0,
    font: 'inherit',
    outline: 'inherit',
    boxShadow: 'none',
  },
  '&:hover': {
    background: Colors.ArmyGreen200,
    borderRadius: '4px',
  },
  '&:hover[disabled], &:focus[disabled]': {
    background: 'none',
  },
};

const popoverWrapper = {
  ml: 'padding_8',
  mt: '-padding_8',
  overflow: 'hidden',
  minHeight: '64px',
  maxHeight: '40vh',
  position: 'absolute',
  left: '0px',
  top: '0px',
  padding: '0',
  boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
  outline: 'none',
  _focus: {
    boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
  },
  '>div:last-of-type': {
    overflow: 'auto',
  },
};

type PopoverComponentProps = {
  children: ReactNode;
  onOpen?: () => void;
  onClose?: () => void;
};

const PopoverComponent = ({
  children,
  onOpen: onOpenCallback,
  onClose: onCloseCallback,
}: PopoverComponentProps) => {
  const { isOpen, onOpen, onClose, onToggle } = useDisclosure();
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Store callbacks in refs to avoid dependency issues
  const onOpenCallbackRef = useRef(onOpenCallback);
  const onCloseCallbackRef = useRef(onCloseCallback);

  // Update refs when callbacks change
  useEffect(() => {
    onOpenCallbackRef.current = onOpenCallback;
  }, [onOpenCallback]);

  useEffect(() => {
    onCloseCallbackRef.current = onCloseCallback;
  }, [onCloseCallback]);

  // Handle open/close with callbacks
  const handleOpen = useCallback(() => {
    onOpen();
    if (onOpenCallbackRef.current) {
      onOpenCallbackRef.current();
    }
  }, [onOpen]);

  const handleClose = useCallback(() => {
    onClose();
    if (onCloseCallbackRef.current) {
      onCloseCallbackRef.current();
    }
  }, [onClose]);

  // Find the main scrollable parent container (FilterToolbar's scrollable area)
  // This finds the scrollable container that's not inside a popover dialog
  const findScrollableParent = useCallback(
    (element: HTMLElement | null): HTMLElement | null => {
      if (!element) return null;

      const style = window.getComputedStyle(element);
      const overflowY = style.overflowY || style.overflow;
      const hasScrollableContent = element.scrollHeight > element.clientHeight;

      // Check if this is a scrollable container
      if ((overflowY === 'auto' || overflowY === 'scroll') && hasScrollableContent) {
        // Check if this container is inside a popover dialog
        // If not, it's likely the main FilterToolbar scrollable container
        const isInsidePopover = element.closest('[role="dialog"]');

        if (!isInsidePopover) {
          // This is the main scrollable container (FilterToolbar)
          return element;
        }

        // If inside a popover, continue searching up to find the main container
        // This handles nested popovers - we want to scroll the main FilterToolbar container
        const mainContainer = findScrollableParent(element.parentElement);

        return mainContainer || element; // Fallback to current if no main container found
      }

      return findScrollableParent(element.parentElement);
    },
    [],
  );

  // Find the popover dialog element
  const findPopoverElement = useCallback((): HTMLElement | null => {
    if (!triggerRef.current) return null;

    // Find the popover by looking for [role="dialog"] elements
    // The popover is rendered as a portal, so we need to search the document
    const allDialogs = document.querySelectorAll('[role="dialog"]');
    const triggerRect = triggerRef.current.getBoundingClientRect();

    // Find the dialog that is visible and positioned to the right of the trigger
    // (matching our "right-start" placement)
    const dialogsArray = Array.from(allDialogs);
    const foundDialog = dialogsArray.find((dialog) => {
      const dialogEl = dialog as HTMLElement;
      const dialogRect = dialogEl.getBoundingClientRect();
      const style = window.getComputedStyle(dialogEl);

      // Check if dialog is visible and positioned correctly
      return (
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        dialogRect.width > 0 &&
        dialogRect.height > 0 &&
        // Check if positioned to the right of trigger (our popover placement)
        dialogRect.left >= triggerRect.right - 10 && // Allow small overlap
        dialogRect.left <= triggerRect.right + 200 && // Within reasonable distance
        Math.abs(dialogRect.top - triggerRect.top) < 200 // Vertically aligned
      );
    });

    return foundDialog ? (foundDialog as HTMLElement) : null;
  }, []);

  // Scroll parent container to bring popover into view
  const scrollParentToShowPopover = useCallback(() => {
    const popoverElement = findPopoverElement();
    if (!popoverElement || !triggerRef.current) return;

    const popoverRect = popoverElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    // Check if popover extends beyond viewport
    const extendsBeyondBottom = popoverRect.bottom > viewportHeight;
    const extendsBeyondTop = popoverRect.top < 0;
    const extendsBeyondRight = popoverRect.right > viewportWidth;
    const extendsBeyondLeft = popoverRect.left < 0;

    if (
      extendsBeyondBottom ||
      extendsBeyondTop ||
      extendsBeyondRight ||
      extendsBeyondLeft
    ) {
      const scrollableParent = findScrollableParent(triggerRef.current.parentElement);

      if (scrollableParent) {
        const parentRect = scrollableParent.getBoundingClientRect();

        // Calculate how much we need to scroll
        let scrollDelta = 0;

        if (extendsBeyondBottom) {
          // Scroll down to show the bottom of the popover
          const popoverBottomRelativeToParent = popoverRect.bottom - parentRect.top;
          scrollDelta = popoverBottomRelativeToParent - scrollableParent.clientHeight;
        } else if (extendsBeyondTop) {
          // Scroll up to show the top of the popover
          const popoverTopRelativeToParent = popoverRect.top - parentRect.top;
          scrollDelta = popoverTopRelativeToParent;
        }

        if (scrollDelta !== 0) {
          scrollableParent.scrollBy({
            top: scrollDelta + 20, // Add some padding
            behavior: 'smooth',
          });
        }
      }
    }
  }, [findScrollableParent, findPopoverElement]);

  // Handle scroll within popover to potentially scroll parent
  useEffect(() => {
    if (!isOpen) {
      return () => {
        // No-op cleanup when popover is closed
      };
    }

    const popoverElement = findPopoverElement();
    if (!popoverElement) {
      return () => {
        // No-op cleanup when popover element not found
      };
    }

    const popoverContent = popoverElement.querySelector(
      'div:last-of-type',
    ) as HTMLElement;
    if (!popoverContent) {
      return () => {
        // No-op cleanup when popover content not found
      };
    }

    const handlePopoverScroll = (e: Event) => {
      const target = e.target as HTMLElement;
      const scrollableParent = findScrollableParent(
        triggerRef.current?.parentElement || null,
      );

      if (!scrollableParent || !popoverElement) return;

      const popoverRect = popoverElement.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      // If scrolling near the top of popover content and popover is near top of viewport
      if (target.scrollTop < 10 && popoverRect.top < 100) {
        scrollableParent.scrollBy({
          top: -20,
          behavior: 'smooth',
        });
      }
      // If scrolling near the bottom of popover content and popover is near bottom of viewport
      else if (
        target.scrollTop + target.clientHeight > target.scrollHeight - 10 &&
        popoverRect.bottom > viewportHeight - 100
      ) {
        scrollableParent.scrollBy({
          top: 20,
          behavior: 'smooth',
        });
      }
    };

    popoverContent.addEventListener('scroll', handlePopoverScroll);

    return () => {
      popoverContent.removeEventListener('scroll', handlePopoverScroll);
    };
  }, [isOpen, findScrollableParent, findPopoverElement]);

  // Scroll parent when popover opens
  useEffect(() => {
    if (!isOpen) {
      return () => {
        // No-op cleanup when popover is closed
      };
    }

    // Use setTimeout to ensure popover is rendered and positioned
    const timeoutId = setTimeout(() => {
      scrollParentToShowPopover();
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [isOpen, scrollParentToShowPopover]);

  return (
    <Popover
      placement="right-start"
      strategy="fixed"
      preventOverflow
      isLazy
      isOpen={isOpen}
      onOpen={handleOpen}
      onClose={handleClose}
    >
      <PopoverTriggerWithChildren>
        <ButtonCK
          ref={triggerRef}
          sx={accordionIconContainer}
          onClick={(e) => {
            // Toggle the popover when clicking the chevron
            onToggle();
            e.stopPropagation();
          }}
        >
          <Icon name={Icon.Name.ChevronRight} fill={Colors.ArmyGreen500} />
        </ButtonCK>
      </PopoverTriggerWithChildren>
      <PopoverContent sx={popoverWrapper}>{children}</PopoverContent>
    </Popover>
  );
};

export default PopoverComponent;
